import axios from 'axios'
import { apiConfig } from '../config'

const BASE_URL = apiConfig.endpoint

// withCredentials makes the browser send the Sanctum session cookie (and the
// XSRF-TOKEN header) on every request — this is what authenticates the SPA now
// that JWT/bearer auth has been removed.
const axiosInstance = axios.create({ baseURL: BASE_URL, withCredentials: true })

const LOGIN_URL = '/authentication/login.html'
const REGISTER_URL = '/authentication/register.html'

// Pages that must render without a session — a background request's 401 here
// must not force a login redirect (unlike authenticated routes).
const PUBLIC_ROUTES = ['/social-media-profiles/meta-setup-guide']

const isOnAuthPage = () =>
    window?.location?.href?.includes(LOGIN_URL) ||
    window?.location?.href?.includes(REGISTER_URL) ||
    window?.location?.href?.includes('/authentication/password-reset')

const isOnPublicRoute = () =>
    PUBLIC_ROUTES.some((route) => window?.location?.pathname === route)

const redirectToLogin = () => {
    if (!isOnAuthPage() && !isOnPublicRoute()) {
        window.location.href = LOGIN_URL
    }
}

const refreshCsrfCookie = () =>
    axiosInstance({
        method: 'GET',
        url: `${BASE_URL}/sanctum/csrf-cookie`
    }).catch(() => null)

/**
 * Runs an axios request with the shared cookie-auth handling that every request
 * type needs: on 419 (CSRF token mismatch) refresh the CSRF cookie and retry
 * once, on 401 redirect to login once, on 503 go to maintenance. `onSuccess`
 * receives the response and returns the caller's result.
 *
 * @param {Object} options - Axios request config.
 * @param {(response: Object) => any} onSuccess - Success handler.
 * @param {string} endopint - Endpoint, used for the 503 maintenance exception.
 * @returns {Promise<any>}
 */
const IMPERSONATION_ENDED_CODES = [
    'impersonation_expired',
    'impersonation_revoked'
]

const isImpersonationEnded = (error) =>
    IMPERSONATION_ENDED_CODES.includes(error.response?.data?.code)

const sendWithAuthHandling = (options, onSuccess, endopint) => {
    let csrfRetried = false

    const run = () =>
        axiosInstance(options)
            .then(onSuccess)
            .catch(async (error) => {
                const status = error.response?.status

                // 419 CSRF token mismatch: refresh the CSRF cookie and retry once.
                if (status === 419 && !csrfRetried) {
                    csrfRetried = true
                    await refreshCsrfCookie()

                    return run()
                }

                // 401 unauthenticated (expired/invalid session): redirect once.
                if (status === 401) {
                    redirectToLogin()
                }

                // The server ended an impersonation session mid-flight (expired or
                // revoked) and restored the admin's own identity. Any endpoint can
                // answer this, so it is handled here rather than per-call: reload so
                // every context, the store and the RTK cache pick up the new identity.
                if (status === 409 && isImpersonationEnded(error)) {
                    window.location.assign('/')
                }

                if (status === 503 && endopint !== '/api/v2/status') {
                    window.location.href = '/maintenance'
                }

                throw error
            })

    return run()
}

/**
 * Retained as a no-op for backward compatibility. Auth is cookie/session based;
 * there is no token interceptor to initialize.
 *
 * @returns {void}
 */
const initialize = () => {}

/**
 * Sends a file upload request to the specified endpoint with the given payload.
 *
 * @param {string} endopint - The API endpoint to which the file upload request is sent.
 * @param {Object} payload - The data payload to be uploaded, typically a FormData object containing the file(s).
 * @returns {Promise} A promise that resolves with the response from the server.
 */
const sentFileUploadRequest = (endopint, payload) => {
    const options = {
        method: 'POST',
        url: `${BASE_URL}${endopint}`,
        data: payload,
        responseType: 'json',
        responseEncoding: 'utf8',
        timeout: 3 * 60000,

        headers: {
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json'
        }
    }

    return sendWithAuthHandling(options, (response) => response, endopint)
}

const cancelTokens = {}

/**
 * Sends an HTTP request to a given endpoint with optional cancel token, timeout, and custom cancel token.
 *
 * @param {string} endpoint - The endpoint to which the request is sent.
 * @param {Object} payload - The payload data to be sent in the request body.
 * @param {boolean} [withCancelToken=false] - Indicates whether to use a cancel token for this request.
 * @param {number|null} [timeout=null] - The timeout duration for the request in milliseconds.
 * @param {Object|null} [customCancelToken=null] - A custom cancel token, if provided.
 * @returns {Promise<Object>} A promise that resolves to the response of the request.
 */
const sentRequest = (
    endopint,
    payload,
    withCancelToken = false,
    timeout = null,
    customCancelToken = null,
    method = 'POST',
    contentType = 'application/x-www-form-urlencoded'
) => {
    if (cancelTokens.hasOwnProperty(endopint)) {
        if (customCancelToken != null) {
            if (customCancelToken != cancelTokens[endopint]) {
                cancelTokens[endopint].cancel(
                    'Operation canceled due to new request.'
                )
            }
        } else {
            cancelTokens[endopint].cancel(
                'Operation canceled due to new request.'
            )
        }
    }

    if (withCancelToken)
        cancelTokens[endopint] = customCancelToken
            ? customCancelToken
            : axios.CancelToken.source()

    const options = {
        method,
        url: `${BASE_URL}${endopint}`,
        data: null,
        payload: null,
        responseType: 'json',
        responseEncoding: 'utf8',
        timeout: timeout ? timeout : 30 * 60 * 1000,

        headers: {
            'Content-Type': contentType,
            Accept: 'application/json'
        },
        cancelToken: withCancelToken ? cancelTokens[endopint].token : null
    }

    if (method === 'GET') {
        options.params = payload
    } else {
        options.data = payload
    }

    return sendWithAuthHandling(
        options,
        (response) => {
            // Legacy responses signal auth failures with a 200 + statusCode in the body.
            const { statusCode: legacyStatusCode } = response.data || {}

            if (legacyStatusCode === 401 || legacyStatusCode === 403) {
                redirectToLogin()
            }

            return response
        },
        endopint
    )
}

/**
 * Sends a POST request to a specified endpoint and triggers a file download from the blob response.
 *
 * @param {string} endopint - The endpoint to which the request will be sent.
 * @param {Object} payload - The data to be sent in the request body.
 * @param {boolean} [withCancelToken=false] - Whether to include a cancel token to allow request cancellation.
 * @param {number|null} [timeout=null] - The request timeout in milliseconds. Defaults to 30 minutes if not provided.
 * @returns {Promise} A promise that resolves when the request completes, triggering a file download.
 */
const sentBlobRequest = (
    endopint,
    payload,
    withCancelToken = false,
    timeout = null
) => {
    if (cancelTokens.hasOwnProperty(endopint)) {
        cancelTokens[endopint].cancel('Operation canceled due to new request.')
    }

    if (withCancelToken) cancelTokens[endopint] = axios.CancelToken.source()

    const options = {
        method: 'POST',
        url: `${BASE_URL}${endopint}`,
        data: payload,
        responseType: 'blob',
        timeout: timeout ? timeout : 30 * 60 * 1000,
        cancelToken: withCancelToken ? cancelTokens[endopint].token : null,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Accept: 'application/json'
        }
    }

    return sendWithAuthHandling(
        options,
        (response) => {
            const fileName =
                response.headers['content-disposition'].split('filename=')[1]
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                // IE variant
                window.navigator.msSaveOrOpenBlob(
                    new Blob([response.data], {
                        type: response.headers['content-type']
                    }),
                    fileName
                )
            } else {
                const url = window.URL.createObjectURL(
                    new Blob([response.data], {
                        type: response.headers['content-type']
                    })
                )
                const link = document.createElement('a')
                link.href = url
                link.setAttribute('download', fileName)
                document.body.appendChild(link)
                link.click()
            }
        },
        endopint
    )
}

/**
 * Clears client-side session state. Under cookie/session auth the server destroys
 * the session via the logout endpoint, so there is nothing to clear locally.
 *
 * @returns {void} No return value.
 */
const clearSession = () => {}

/**
 * Generates a response object for errors encountered during operations.
 *
 * @param {Error} err - The error object caught during an operation.
 * @returns {Object} A response object containing details about the error, including status code and error message.
 */
const getCatchResponse = (err) => ({
    body: {},
    statusCode: -1,
    errorMessage:
        err.message !== 'Operation canceled due to new request.'
            ? err.message
            : null,
    error_stack: null,
    debug: null
})

export default {
    clearSession,
    sentRequest,
    sentBlobRequest,
    sentFileUploadRequest,
    initialize,
    getCatchResponse
}
